import isEmpty from 'lodash/isEmpty';
import type {
	AccountId,
	IssueId,
	VersionId,
	IssueTypeId,
} from '@atlassian/jira-shared-types/src/general.tsx';
import { createStore, createHook, type Action } from '@atlassian/react-sweet-state';
import type { UNASSIGNED_EPIC_ID } from '../../common/constants';
import type { EnvironmentName, EnvironmentTypeConcatName } from '../../common/types';

export type EpicIdOption = IssueId | typeof UNASSIGNED_EPIC_ID;

// Note: an empty string/array filter value means "do not filter"
export type State = {
	text: string;
	assignees: AccountId[];
	environmentNames: EnvironmentName[];
	environmentTypeConcatNames: EnvironmentTypeConcatName[];
	epicIds: EpicIdOption[];
	issueTypeIds: IssueTypeId[];
	fixVersions: VersionId[];
};

export const initialState: State = {
	text: '',
	assignees: [],
	environmentNames: [],
	environmentTypeConcatNames: [],
	epicIds: [],
	issueTypeIds: [],
	fixVersions: [],
};

export const actions = {
	setEpicIdsFilter:
		(epicIds: EpicIdOption[]): Action<State> =>
		({ setState }) =>
			setState({ epicIds }),
	setIssueTypeIdsFilter:
		(issueTypeIds: IssueTypeId[]): Action<State> =>
		({ setState }) =>
			setState({ issueTypeIds }),
	setTextFilter:
		(text: string): Action<State> =>
		({ setState }) =>
			setState({ text }),
	toggleAssignee:
		(assignee: AccountId): Action<State> =>
		({ setState, getState }) => {
			const { assignees } = getState();
			if (assignees.includes(assignee)) {
				setState({ assignees: assignees.filter((accountId) => accountId !== assignee) });
			} else {
				setState({ assignees: [...assignees, assignee] });
			}
		},
	setEnvironmentFilter:
		(environmentNames: EnvironmentName[]): Action<State> =>
		({ setState }) =>
			setState({ environmentNames }),
	setEnvironmentTypeConcatNameFilter:
		(environmentTypeConcatNames: EnvironmentTypeConcatName[]): Action<State> =>
		({ setState }) =>
			setState({ environmentTypeConcatNames }),
	setFixVersionFilter:
		(fixVersions: VersionId[]): Action<State> =>
		({ setState }) =>
			setState({ fixVersions }),
	setFilter:
		(filter: State): Action<State> =>
		({ setState }) =>
			setState(filter),
	resetAllFilters:
		(): Action<State> =>
		({ setState }) =>
			setState(initialState),
} as const;

type Actions = typeof actions;

const Store = createStore<State, Actions>({
	name: 'useTableFilterStore',
	initialState,
	actions,
});

export const useTableFilterStore = createHook(Store);

export const useHasAppliedFilters = createHook(Store, {
	selector: (state) => Object.values(state).some((val) => !isEmpty(val)),
});

export const useEnvironmentNamesFilter = createHook(Store, {
	selector: (state) => state.environmentNames,
});

export const useEnvironmentTypeConcatNamesFilter = createHook(Store, {
	selector: (state) => state.environmentTypeConcatNames,
});

export const useEpicIdsFilter = createHook(Store, {
	selector: (state) => state.epicIds,
});

export const useIssueTypeIdsFilter = createHook(Store, {
	selector: (state) => state.issueTypeIds,
});

export const useFixVersionsFilter = createHook(Store, {
	selector: (state) => state.fixVersions,
});
